import {useCallback, useState} from 'react';
import {
  applySharedAppearance,
  isAccentName,
  readSharedAppearance,
  toFontSizeName,
  writeSharedAppearance,
  type AccentName,
  type AppearanceMode,
  type CornerName,
  type FontSizeName
} from '@kroll-resolver-crisp/ui-components-react';

// The accent, font size, corners and mode the Appearance dialog offers. Since ui-components-react 3.0 they
// are shared across every Crisp app on the domain through the `crisp-appearance` cookie (mirrored
// to this origin's localStorage for when cookies are blocked), so a choice made here follows the
// analyst to the other apps and back. ui-tokens keys accent and size off attributes on <html>:
// `data-accent` picks a token block, `data-font-size` scales the root font size, `data-corners`
// squares the radius steps; the defaults (cyan, compact, rounded) are what no attribute means. Mode is next-themes' - see `rememberMode`.
//
// Deliberately not a second next-themes provider: nested providers share one context, so every
// useTheme() below it would read an accent where it expects a mode.

// The per-app keys PRI wrote before the shared store existed. Still read, so an analyst who chose
// before the upgrade keeps that choice until they make a new one; never written again.
export const ACCENT_STORAGE_KEY = 'accent';
export const FONT_SIZE_STORAGE_KEY = 'fontSize';

// The key next-themes reads, as given to `ThemeProvider` in main.tsx. It reads this before first
// paint, which is how the shared mode reaches it without a second mode store.
export const THEME_STORAGE_KEY = 'theme';

const DEFAULT_ACCENT: AccentName = 'cyan';
const DEFAULT_FONT_SIZE: FontSizeName = 'compact';
const DEFAULT_CORNERS: CornerName = 'rounded';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Storage can be unavailable (privacy mode, blocked site data); fall back to the default.
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not persisted, but still applied for this session.
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

/**
 The accent to apply: the shared choice when the analyst has made one anywhere on the domain,
 else PRI's own pre-3.0 key, else the default. A stale or hand-edited value (an accent a later
 release dropped, a typo) reads as the default rather than writing an attribute no token block
 matches - the package validates the shared store, and `isAccentName` covers the legacy key.
 */
export function storedAccent(): AccentName {
  const shared = readSharedAppearance().accent;
  if (shared !== undefined) {
    return shared;
  }

  const legacy = read(ACCENT_STORAGE_KEY);
  return isAccentName(legacy) ? legacy : DEFAULT_ACCENT;
}

export function storedFontSize(): FontSizeName {
  const shared = readSharedAppearance().fontSize;
  if (shared !== undefined) {
    return shared;
  }

  // The legacy key holds 3.x's names (`default`, `comfortable`); toFontSizeName maps them onto
  // 4.0's the same way the shared store does.
  return toFontSizeName(read(FONT_SIZE_STORAGE_KEY)) ?? DEFAULT_FONT_SIZE;
}

/** Corners arrived with ui-components-react 4.0, after the per-app keys, so there is no legacy key. */
export function storedCorners(): CornerName {
  return readSharedAppearance().corners ?? DEFAULT_CORNERS;
}

/**
 Puts the stored accent, font size and corners on <html>, and hands the shared mode to next-themes by
 writing its storage key. main.tsx calls it before React's first render: until then #root is empty
 and the page is a bare neutral background, which neither attribute changes, so nothing
 accent-coloured or sized can paint first. That is why the design system's blocking <head> script
 is not used here - the web CSP (`script-src 'self'`) refuses inline scripts, and this runs just
 as early for an SPA.
 */
export function applyStoredAppearance(): void {
  applySharedAppearance({
    accent: storedAccent(),
    fontSize: storedFontSize(),
    corners: storedCorners()
  });

  const {mode} = readSharedAppearance();
  if (mode !== undefined) {
    write(THEME_STORAGE_KEY, mode);
  }
}

/**
 Persists a mode choice for every app on the domain. next-themes keeps PRI's own copy under
 `THEME_STORAGE_KEY` when `setTheme` runs; this is the half that travels.
 */
export function rememberMode(mode: AppearanceMode): void {
  writeSharedAppearance({mode});
}

/**
 The accent, font size and corners for the Appearance dialog's controlled props. Each setter applies the
 attribute and persists the choice in one step, so the dialog's "applies as you pick" behaviour
 matches the mode row's. A new choice retires the legacy per-app key, so there is one source.
 */
export function useAppearancePreferences(): {
  accent: AccentName;
  setAccent: (accent: AccentName) => void;
  fontSize: FontSizeName;
  setFontSize: (fontSize: FontSizeName) => void;
  corners: CornerName;
  setCorners: (corners: CornerName) => void;
} {
  const [accent, setAccentState] = useState(storedAccent);
  const [fontSize, setFontSizeState] = useState(storedFontSize);
  const [corners, setCornersState] = useState(storedCorners);

  const setAccent = useCallback((next: AccentName) => {
    applySharedAppearance({accent: next});
    writeSharedAppearance({accent: next});
    remove(ACCENT_STORAGE_KEY);
    setAccentState(next);
  }, []);

  const setFontSize = useCallback((next: FontSizeName) => {
    applySharedAppearance({fontSize: next});
    writeSharedAppearance({fontSize: next});
    remove(FONT_SIZE_STORAGE_KEY);
    setFontSizeState(next);
  }, []);

  const setCorners = useCallback((next: CornerName) => {
    applySharedAppearance({corners: next});
    writeSharedAppearance({corners: next});
    setCornersState(next);
  }, []);

  return {accent, setAccent, fontSize, setFontSize, corners, setCorners};
}
